import { useEffect, useState } from "react";
import {
  Button,
  SpaceBetween,
  Header,
  Popover,
  StatusIndicator,
  ButtonDropdown,
  Badge,
  KeyValuePairs,
} from "@cloudscape-design/components";
import Countdown from 'react-countdown';
import {
  getDeploymentStatusType,
  getPaymentInformationLabel,
} from "../utils/Utils";
import {
  DeploymentItem,
} from "../types/deploymentTypes";
import { useBreadcrumbs } from "../contexts/BreadcrumbContext";
import { useHelpPanel } from "contexts/HelpPanelContext";
import { DeploymentOverview } from "../components/deployments/DeploymentOverview";
import DeploymentPage from "components/deployments/DeploymentPage";
import { dedicatedColumnDisplayOrder } from "configs/settings";
import { useNavigate } from "react-router-dom";
import deploymentService from "services/deploymentService";
import { useUserContext } from "contexts/UserContext";
import { useNotification } from "contexts/NotificationContext";
import { faUserShield } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import AppConfig from "configs/AppConfig";
import DeleteConfirmationModal from "components/shared/DeleteConfirmationModal";
import { freeTrialPeriodDays } from "configs/settings";

const DeploymentsDedicatedClusters = () => {
  const navigate = useNavigate();
  const { setHelpPanelContent } = useHelpPanel();
  const { setBreadcrumbs } = useBreadcrumbs();
  const { userId } = useUserContext();

  const [deploymentsList, setDeploymentsList] = useState<DeploymentItem[]>([]);
  const [loadingDedicatedClusters, setLoadingDedicatedClusters] = useState(false);
  const [loadingDedicatedClustersError, setLoadingDedicatedClustersError] = useState<string | null>(null);
  const { addNotification } = useNotification();

  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [isCancelingSubsctiption, setIsCancelingSubscription] = useState<boolean>(false);
  const [clusterToCancelSubscription, setClusterToCancelSubscription] = useState<DeploymentItem | null>(null);
  


  const fetchDedicatedDeploymentsList = async () => {
    try {
      setLoadingDedicatedClusters(true);
      setLoadingDedicatedClustersError(null);

      // Fetch dedicated clusters
      const deployments = await deploymentService.fetchDedicatedClusters();

      // Check health for each cluster
      // const deploymentsWithHealth = await Promise.all(
      //   deployments.map(async (deployment) => ({
      //     ...deployment,
      //     health: await deploymentService.checkDeploymentHealth(deployment),
      //   }))
      // );

      const deploymentsWithHealth = deployments.map((deployment) => ({
        ...deployment,
        health: "--",
      }));

      // Update state with health info
      setDeploymentsList(deploymentsWithHealth);
    } catch (error) {
      setLoadingDedicatedClustersError(
        "Failed to load clusters. Please try again later."
      );
    } finally {
      setLoadingDedicatedClusters(false);
    }
  };

  const dedicatedClusterContent = (
    <DeploymentOverview
      title="Dedicated Cluster"
      description="A dedicated cluster is reserved exclusively for your organization. It is designed for production-grade workloads and supports higher scalability and reliability. Dedicated clusters can be customized for specific needs (multi-region, separate prod + dev environments, and more)."
    />
  );

  const handleCancelSubsctiptionClick = (cluster: DeploymentItem)=>{
    setClusterToCancelSubscription(cluster);
    setShowDeleteModal(true);
  }

  const cancelSubscriptionRequest = async ()=>{
    if(!clusterToCancelSubscription) return;

    setIsCancelingSubscription(true);
    try {
      await deploymentService.cancelSubscription(clusterToCancelSubscription?.id ?? "");
      addNotification("success", "Subscription canceled. The cluster will be inactive at the end of the billing period.");
      fetchDedicatedDeploymentsList(); // fetch clusters

    } catch (error) {
      console.error(error);
      addNotification("error", "Unable to cancel the subscription. Please try again.");

    } finally {
      setIsCancelingSubscription(false);
      setShowDeleteModal(false);
      setClusterToCancelSubscription(null);
    }
  }

  const handleOpenCreatePage = () => {

  const targetUrl = "/dedicated-clusters/create";

  if (typeof (window as any).gtag_report_conversion === "function") {
    (window as any).gtag_report_conversion(window.location.origin + targetUrl);
  } else {
    navigate(targetUrl);
  }
};

  const dedicatedColumnDefinitions = [
    {
      id: "name",
      header: "Name",
      cell: (item: DeploymentItem) => {
        const checkForOwner = item.creator_id === userId;
        const checkForFreeTrial = item?.payment_status?.status === "trial";
        return <div className="inline-space">
          <div 
            className="clickable text-underline"
            onClick={()=>{
              navigate(`/dedicated-clusters/${item.id}`);
            }}
          >
            {item.name}
          </div> 

          {checkForOwner &&
            <Badge color="blue">
              <div className="owner-icon-badge">
                <FontAwesomeIcon
                  icon={faUserShield as IconProp}
                />
              </div>
            </Badge>
          }
          {checkForFreeTrial &&
            <Badge color="severity-low">
              <span className="no-wrap-text">
                Free trial:{" "} 
                <Countdown 
                  date={new Date(item.created_at).getTime() + freeTrialPeriodDays * 24 * 60 * 60 * 1000} // adds free trial period days 
                  renderer={({ days }) => <span>{days} {days === 1 ? "day" : "days"}</span>}
                />
              </span>
            </Badge>
          }
        </div>
      },
      sortingField: "name",
    },
    {
      id: "region",
      header: "Region",
      cell: (item: DeploymentItem) => item.region,
      sortingField: "region",
    },

    {
      id: "state",
      header: "State",
      cell: (item: DeploymentItem) => (
        <StatusIndicator type={getDeploymentStatusType(item.status)}>
          {item.status}
        </StatusIndicator>
      ),
    },
    {
      id: "users",
      header: "Users",
      cell: (item: DeploymentItem) => item.users,
      sortingField: "users",
    },
    {
      id: "sessions",
      header: "Sessions",
      cell: (item: DeploymentItem) => item.sessions,
    },
    {
      id: "domainName",
      header: "Domain Name",
      cell: (item: DeploymentItem) => item.domainName,
      sortingField: "domainName",
    },
    {
      id: "idps",
      header: "IdPs",
      cell: (item: DeploymentItem) => item.idps,
    },
    {
      id: "healthy",
      header: "Healthy",
      cell: () => "--",
      // cell: (item: DeploymentItem) => (
      //   <StatusIndicator
      //     type={item.health ? "success" : "error"}
      //   ></StatusIndicator>
      // ),
    },
    {
      id: "actions",
      header: "Actions",
      cell: (item: DeploymentItem) => {
        // const isDeploymentCreator = item.creator_id === userId;
        return (
          <div className="action-column-content">
            <Button
              variant="link"
              iconAlign="right"
              iconName="external"
              onClick={() =>window.open(item?.console_url, "_blank")}
              wrapText={false}
              disabled = {item.status !== "active"}
            >
              Console
            </Button>

            <ButtonDropdown
              items={[
                { id: "cancel-subscription", text: "Cancel Subscription", disabled: item.payment_status?.status === "canceled" }
              ]}
              expandToViewport
              variant="icon"
              ariaLabel="Actions"
              onItemClick={({detail})=>{
                const selected = detail.id;
                switch(selected){
                  case "cancel-subscription":
                    handleCancelSubsctiptionClick(item);
                    break;
                  default:
                    break;
                }
              }}
            />

            <Popover
              dismissButton={false}
              position="top"
              size="small"
              header="Cluster Information"
              triggerType="custom"
              content={
                <KeyValuePairs
                  columns={1}
                  items={[
                    {
                      label: "Payment status:",
                      value:
                        item?.payment_status?.status === "trial" ? (
                          <>
                            Free trial:{" "}
                            <Countdown date={new Date(item.created_at).getTime() + freeTrialPeriodDays * 24 * 60 * 60 * 1000} />
                          </>
                        ) : (
                          getPaymentInformationLabel("status", item?.payment_status?.status)
                        ),
                    },
                    {
                      label: "Plan:",
                      value: getPaymentInformationLabel("plan", item?.payment_status?.plan)
                    },
                    {
                      label: "Size:",
                      value: getPaymentInformationLabel("size", item?.payment_status?.size)
                    },
                    {
                      label: "Billing cycle:",
                      value: getPaymentInformationLabel("billing_type", item?.payment_status?.billing_type)
                    }
                  ]}
                />
              }
            >
              <Button variant="icon" iconName="status-info"></Button>
            </Popover>
          </div>
        )
      },
    },
  ];

  useEffect(() => {
    fetchDedicatedDeploymentsList();
  },[])

  useEffect(() => {
    setBreadcrumbs([
      { text: AppConfig.APP_NAME, href: "/" },
      { text: "Dedicated Clusters", href: "/dedicated-clusters" },
    ]);

    setHelpPanelContent({
      header: "Overview",
      content: dedicatedClusterContent,
    });

    //   Cleanup when component unmounts
    return () => {
      setBreadcrumbs([]);
    };
  }, [setHelpPanelContent, setBreadcrumbs]);


  return (
    <SpaceBetween size="l">

      <Header variant="h2">Deployments</Header>

      <DeploymentPage
        deploymentType="dedicated"
        tableDescription="List of all Dedicated Clusters"
        badgeText="Dedicated"
        title="Dedicated Clusters"
        createButtonText="Create Dedicated Cluster"
        handleOpenCreatePage={handleOpenCreatePage}
        deploymentsList={deploymentsList}
        columnDefinitions={dedicatedColumnDefinitions}
        columnDisplayOrder={dedicatedColumnDisplayOrder}
        isLoadingDeployments={loadingDedicatedClusters}
        loadingDeploymentsError={loadingDedicatedClustersError}
        handleLoadingData={fetchDedicatedDeploymentsList}
      />

      <DeleteConfirmationModal 
        isVisible={showDeleteModal} 
        isDeleting={isCancelingSubsctiption} 
        headerText="Cancel Subscription"
        headerIconName="status-stopped"
        mainButtonText="Cancel Subscription"
        mainButtonLoadingText="Cancelling Subscription..."
        onDismiss={()=>{setShowDeleteModal(false)}} 
        onConfirm={cancelSubscriptionRequest} 
        deleteConfirmationValue={"cancel subscription"} 
        deleteAction="cancellation"
        showAlertBox={true}
        alertBoxTitle="Important Notice:"
        alertBoxContent={<>Your Cluster will remain active until the end of your current billing period. Once your legal subscription term ends, the associated cluster and its stored data will be permanently deleted in accordance with our <a target="_blank" href={`${AppConfig.ASWAR_DOCUMENTATION_URL}docs/compliance/data-deletion-exit-policy`}>Data Deletion & Exit Policy</a>. Please review the policy for more details.</>}
      >
        Are you sure you want to cancel your subscription for cluster: <b>{clusterToCancelSubscription?.name}</b> ?
      </DeleteConfirmationModal>
    </SpaceBetween>
  );
};

export default DeploymentsDedicatedClusters;
